import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ExternalLink, Send, Calendar as CalIcon, Building2, XCircle, Pencil, CheckCircle2, Loader2, Download, Clock, MapPin, Copy, UserPlus, Mail, AlertTriangle, Info, Receipt, Plus, Trash2, FileText, Wallet, RotateCcw, Car, ChevronDown, ChevronRight } from "lucide-react";

const LOGO = "/logo-aae.png";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  contrat_a_faire: "Contrat à faire",
  dpae_faite: "DPAE faite",
  envoye: "Contrat envoyé",
  signe: "Contrat signé",
  paie_envoyee: "Paie envoyée",
  aem: "AEM envoyé",
  dsn: "DSN faite",
  annule: "Contrat annulé",
};

const STATUS_CLASS = {
  envoye: "bg-yellow-500/15 text-yellow-200 border-yellow-400",
  signe: "bg-green-500/15 text-green-200 border-green-500",
  annule: "bg-red-500/15 text-red-200 border-red-400 line-through decoration-red-500/60",
};

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const DAYS_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
function periodLabel(p) {
  const [y, m] = (p || "").split("-").map(Number);
  if (!y || !m) return p || "—";
  return `${MONTHS_FR[m - 1]} ${y}`;
}
function frDate(iso) {
  if (!iso || iso.length < 10) return "—";
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}
function longDateLabel(iso) {
  if (!iso || iso.length < 10) return "Date non renseignée";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d)) return iso;
  return `${DAYS_FR[d.getDay()]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}
// Regroupe les déclarations d'un mois : d'abord par date (ordre décroissant : fin de mois → début), puis par événement.
function groupByDateThenEvent(declarations) {
  const sorted = [...(declarations || [])].sort((a, b) =>
    (b.start_date || "").localeCompare(a.start_date || "") ||
    (a.event_name || "").localeCompare(b.event_name || "")
  );
  const byDate = new Map();
  sorted.forEach(d => {
    const dateKey = d.start_date || "";
    if (!byDate.has(dateKey)) byDate.set(dateKey, new Map());
    const eventKey = d.event_id || d.event_name || "__no_event__";
    const dm = byDate.get(dateKey);
    if (!dm.has(eventKey)) dm.set(eventKey, {
      event_name: d.event_name || "",
      event_location: d.event_location || "",
      declarations: [],
      total_ht: 0,
    });
    const g = dm.get(eventKey);
    g.declarations.push(d);
    // Les contrats annulés ne sont pas comptés dans le sous-total HT de l'événement
    if (d.status !== "annule") g.total_ht += Number(d.cost_ht_client || 0);
  });
  return Array.from(byDate.entries()).map(([date, evMap]) => {
    const events = Array.from(evMap.values());
    return {
      date,
      events,
      total_ht: events.reduce((s, ev) => s + ev.total_ht, 0),
      declarations_count: events.reduce((s, ev) => s + ev.declarations.length, 0),
    };
  });
}

export default function PublicClientPortal() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeForm, setChangeForm] = useState({ declaration_id: "", action: "modify", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "" });
  const [inviteSending, setInviteSending] = useState(false);

  // ---- Frais (portail intermittent uniquement) ----
  const EXPENSE_TYPES = [
    { value: "transport_train",    label: "Train",                     category: "Déplacement" },
    { value: "transport_avion",    label: "Avion",                     category: "Déplacement" },
    { value: "transport_taxi",     label: "Taxi",                      category: "Déplacement" },
    { value: "transport_parking",  label: "Parking",                   category: "Déplacement" },
    { value: "transport_peage",    label: "Péage",                     category: "Déplacement" },
    { value: "transport_voiture",  label: "Voiture perso (frais km)",  category: "Déplacement" },
    { value: "transport_location", label: "Voiture de location",       category: "Déplacement" },
    { value: "hebergement",        label: "Hébergement",               category: "Hébergement" },
    { value: "restauration",       label: "Restauration",              category: "Restauration" },
  ];
  const defaultKm = state.data?.intermittent_settings?.default_cost_per_km ?? 0.55;
  const EMPTY_EXPENSE = { title: "", expense_type: "transport_train", amount: "", km_count: "", cost_per_km: String(defaultKm) };
  const [expensesOpen, setExpensesOpen] = useState(false);
  // Dialogue "Inscription intermittent" (portail entreprise)
  const INSCRIPTION_URL = "https://compagnon.antoineevenements.fr/declaration/inscription-intermittent";
  const [inscOpen, setInscOpen] = useState(false);
  const [inscEmail, setInscEmail] = useState("");
  const [inscSending, setInscSending] = useState(false);
  const copyInscriptionLink = async () => {
    try { await navigator.clipboard.writeText(INSCRIPTION_URL); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };
  const sendInscriptionEmail = async () => {
    const email = (inscEmail || "").trim();
    if (!email || !email.includes("@")) { toast.error("Email invalide"); return; }
    setInscSending(true);
    try {
      await axios.post(`${API_BASE}/public-links/${token}/send-inscription-email`, { to_email: email });
      toast.success(`Lien envoyé à ${email}`);
      setInscOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setInscSending(false);
    }
  };
  const [expensesDecl, setExpensesDecl] = useState(null); // declaration en cours de gestion
  const [expensesData, setExpensesData] = useState({ expenses: [], totals_by_type: [], total: 0 });
  const [expensesLoading, setExpensesLoading] = useState(false);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE);
  const [receiptFile, setReceiptFile] = useState(null);
  const [savingExpense, setSavingExpense] = useState(false);
  // Édition inline d'un frais existant
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EXPENSE);
  const [editReceiptFile, setEditReceiptFile] = useState(null);      // nouveau fichier uploadé (remplace l'ancien)
  const [editReceiptCleared, setEditReceiptCleared] = useState(false); // l'utilisateur a demandé à retirer le justificatif existant
  const [savingEdit, setSavingEdit] = useState(false);
  // Clôture des frais du contrat : "J'ai déposé tous mes frais"
  const [markingSubmitted, setMarkingSubmitted] = useState(false);

  const openExpenses = async (d) => {
    setExpensesDecl(d);
    setExpenseForm(EMPTY_EXPENSE);
    setReceiptFile(null);
    setExpensesOpen(true);
    await loadExpenses(d.id);
  };

  const loadExpenses = async (declId) => {
    setExpensesLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/public-links/${token}/portal/expenses`, { params: { declaration_id: declId } });
      setExpensesData(data);
    } catch (e) {
      toast.error("Impossible de charger les frais");
    } finally { setExpensesLoading(false); }
  };

  const submitExpense = async () => {
    if (!expenseForm.title?.trim()) { toast.error("Titre du frais requis"); return; }    const isCar = expenseForm.expense_type === "transport_voiture";
    if (isCar) {
      if (!(parseFloat(expenseForm.km_count) > 0)) { toast.error("Nombre de kilomètres requis (> 0)"); return; }
      if (!(parseFloat(expenseForm.cost_per_km) > 0)) { toast.error("Coût par km requis (> 0)"); return; }
    } else if (!(parseFloat(expenseForm.amount) > 0)) {
      toast.error("Montant du frais requis (> 0)"); return;
    }
    setSavingExpense(true);
    try {
      let receipt_file_id = "";
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        const up = await axios.post(`${API_BASE}/public-links/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_file_id = up.data?.id || "";
      }
      const payload = {
        declaration_id: expensesDecl.id,
        title: expenseForm.title.trim(),
        expense_type: expenseForm.expense_type,
        amount: isCar ? 0 : parseFloat(expenseForm.amount) || 0,
        km_count: isCar ? parseFloat(expenseForm.km_count) || 0 : 0,
        cost_per_km: isCar ? parseFloat(expenseForm.cost_per_km) || 0 : 0,
        receipt_file_id,
      };
      await axios.post(`${API_BASE}/public-links/${token}/portal/expenses`, payload);
      toast.success("Frais ajouté");
      setExpenseForm(EMPTY_EXPENSE);
      setReceiptFile(null);
      await loadExpenses(expensesDecl.id);
      load(); // refresh totaux
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout du frais");
    } finally { setSavingExpense(false); }
  };

  const deleteExpense = async (eid) => {
    if (!window.confirm("Supprimer ce frais ?")) return;
    try {
      await axios.delete(`${API_BASE}/public-links/${token}/portal/expenses/${eid}`);
      toast.success("Frais supprimé");
      await loadExpenses(expensesDecl.id);
      load();
    } catch (e) { toast.error("Erreur lors de la suppression"); }
  };

  // ---- Édition inline d'un frais ----
  const startEditExpense = (e) => {
    setEditingId(e.id);
    setEditForm({
      title: e.title || "",
      expense_type: e.expense_type || "transport_train",
      amount: e.expense_type === "transport_voiture" ? "" : (e.amount ?? ""),
      km_count: e.km_count ?? "",
      cost_per_km: e.cost_per_km ?? String(defaultKm),
    });
    setEditReceiptFile(null);
    setEditReceiptCleared(false);
  };
  const cancelEditExpense = () => {
    setEditingId(null);
    setEditReceiptFile(null);
    setEditReceiptCleared(false);
  };

  // Marque le contrat comme "tous les frais déposés" (ou rouvre la saisie)
  const toggleExpensesSubmitted = async () => {
    if (!expensesDecl) return;
    const currently = expensesDecl.expenses_submitted === true;
    const willSubmit = !currently;
    if (willSubmit && (expensesData.expenses || []).length === 0) {
      if (!window.confirm("Vous confirmez qu'aucun frais n'est à déclarer pour ce contrat ?")) return;
    }
    setMarkingSubmitted(true);
    try {
      await axios.post(
        `${API_BASE}/public-links/${token}/portal/declarations/${expensesDecl.id}/expenses-submitted`,
        { submitted: willSubmit }
      );
      toast.success(willSubmit ? "Contrat clôturé : tous les frais sont déposés" : "Saisie de frais rouverte");
      // Mise à jour locale immédiate + rechargement portail
      setExpensesDecl(d => ({ ...d, expenses_submitted: willSubmit }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la clôture");
    } finally { setMarkingSubmitted(false); }
  };
  const submitEditExpense = async (original) => {
    if (!editForm.title?.trim()) { toast.error("Titre du frais requis"); return; }
    const isCar = editForm.expense_type === "transport_voiture";
    if (isCar) {
      if (!(parseFloat(editForm.km_count) > 0)) { toast.error("Nombre de kilomètres requis (> 0)"); return; }
      if (!(parseFloat(editForm.cost_per_km) > 0)) { toast.error("Coût par km requis (> 0)"); return; }
    } else if (!(parseFloat(editForm.amount) > 0)) {
      toast.error("Montant du frais requis (> 0)"); return;
    }
    setSavingEdit(true);
    try {
      // Détermine le receipt_file_id à envoyer
      let receipt_file_id = original.receipt_file_id || "";
      if (editReceiptFile) {
        const fd = new FormData();
        fd.append("file", editReceiptFile);
        const up = await axios.post(`${API_BASE}/public-links/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        receipt_file_id = up.data?.id || "";
      } else if (editReceiptCleared) {
        receipt_file_id = "";
      }
      const payload = {
        declaration_id: expensesDecl.id,
        title: editForm.title.trim(),
        expense_type: editForm.expense_type,
        amount: isCar ? 0 : parseFloat(editForm.amount) || 0,
        km_count: isCar ? parseFloat(editForm.km_count) || 0 : 0,
        cost_per_km: isCar ? parseFloat(editForm.cost_per_km) || 0 : 0,
        receipt_file_id,
      };
      await axios.patch(`${API_BASE}/public-links/${token}/portal/expenses/${original.id}`, payload);
      toast.success("Frais modifié");
      cancelEditExpense();
      await loadExpenses(expensesDecl.id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la modification");
    } finally { setSavingEdit(false); }
  };

  const sendInvite = async () => {
    if (!inviteForm.email || !inviteForm.email.includes("@")) { toast.error("Adresse email invalide"); return; }
    setInviteSending(true);
    try {
      await axios.post(`${API_BASE}/public-links/${token}/portal/send-registration`, inviteForm);
      toast.success(`Invitation envoyée à ${inviteForm.email}`);
      setInviteOpen(false);
      setInviteForm({ email: "", name: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setInviteSending(false); }
  };

  const load = () => {
    setState(s => ({ ...s, loading: true }));
    axios.get(`${API_BASE}/public-links/${token}/portal`)
      .then(({ data }) => setState({ loading: false, error: "", data }))
      .catch(e => setState({ loading: false, error: e?.response?.data?.detail || "Lien invalide", data: null }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const openChange = (d, action) => {
    setChangeForm({ declaration_id: d.id, action, message: "" });
    setChangeOpen(true);
  };

  const submitChange = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/public-links/${token}/portal/change-request`, {
        declaration_id: changeForm.declaration_id,
        action: changeForm.action,
        message: changeForm.message,
      });
      toast.success("Demande envoyée à l'équipe AAE");
      setChangeOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  const downloadMonthPdf = async (period) => {
    try {
      const res = await axios.get(`${API_BASE}/public-links/${token}/portal/pdf`, {
        params: { period }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AAE_${(client?.name || "client").replace(/\s+/g, "_")}_${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Impossible de générer le PDF");
    }
  };

  const grandTotal = state.data?.total_ht_all || 0;
  const grandExpenses = state.data?.total_expenses_all || 0;
  const isIntermittentPortal = !!state.data?.is_intermittent_portal;
  const client = state.data?.client;
  const color = "#D5F51A"; // Couleur lime du site — utilisée pour tous les accents du portail. La couleur du client est réservée aux tuiles du Kanban Déclarations (app authentifiée).

  // ---- Intermittent portal : settings (renewal + km) + filtre mois/année ----
  const settings = state.data?.intermittent_settings || { renewal_date: "", default_cost_per_km: 0.55 };
  const cachetCount = state.data?.cachet_count_year || 0;
  const cachetWindow = state.data?.cachet_window || null;
  // renewal_date est stocké au format "MM-DD" côté serveur ; on éclate en jour/mois pour l'UI
  const [settingsForm, setSettingsForm] = useState({ renewal_day: "", renewal_month: "", default_cost_per_km: "0.55" });
  const [savingSettings, setSavingSettings] = useState(false);
  const parseRenewal = (r) => {
    if (!r) return { d: "", m: "" };
    // Formats acceptés : "MM-DD" ou "YYYY-MM-DD"
    if (r.length === 5 && r[2] === "-") return { m: r.slice(0, 2), d: r.slice(3, 5) };
    if (r.length >= 10 && r[4] === "-") return { m: r.slice(5, 7), d: r.slice(8, 10) };
    return { d: "", m: "" };
  };
  const composedRenewal = useMemo(() => {
    const d = settingsForm.renewal_day, m = settingsForm.renewal_month;
    if (!d && !m) return "";
    if (!d || !m) return null; // partiel → invalide
    return `${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }, [settingsForm]);
  const currentRenewal = useMemo(() => {
    const { d, m } = parseRenewal(settings.renewal_date || "");
    return d && m ? `${m}-${d}` : "";
  }, [settings.renewal_date]);
  const settingsDirty = useMemo(() => {
    if (!isIntermittentPortal) return false;
    return (composedRenewal !== null && composedRenewal !== currentRenewal)
        || String(settingsForm.default_cost_per_km) !== String(settings.default_cost_per_km);
  }, [composedRenewal, currentRenewal, settingsForm.default_cost_per_km, settings.default_cost_per_km, isIntermittentPortal]);
  useEffect(() => {
    if (isIntermittentPortal) {
      const { d, m } = parseRenewal(settings.renewal_date || "");
      setSettingsForm({
        renewal_day: d,
        renewal_month: m,
        default_cost_per_km: String(settings.default_cost_per_km ?? 0.55),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data?.intermittent_settings?.renewal_date, state.data?.intermittent_settings?.default_cost_per_km, isIntermittentPortal]);

  const saveSettings = async () => {
    if (composedRenewal === null) { toast.error("Jour ET mois requis (ou vider les deux)"); return; }
    setSavingSettings(true);
    try {
      const payload = {
        renewal_date: composedRenewal,
        default_cost_per_km: parseFloat(settingsForm.default_cost_per_km || "0") || 0,
      };
      await axios.patch(`${API_BASE}/public-links/${token}/intermittent-settings`, payload);
      toast.success("Réglages enregistrés");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'enregistrer les réglages");
    } finally { setSavingSettings(false); }
  };

  // Sélection mois/année pour filtrer l'affichage
  const now = new Date();
  const [selMonth, setSelMonth] = useState("all"); // "all" | "01".."12"
  const [selYear, setSelYear] = useState(String(now.getFullYear()));
  const availableYears = useMemo(() => {
    const years = new Set([String(now.getFullYear())]);
    (state.data?.months || []).forEach(m => {
      const y = (m.period || "").slice(0, 4);
      if (y) years.add(y);
    });
    return Array.from(years).sort();
  }, [state.data?.months, now]);
  const filteredMonths = useMemo(() => {
    return (state.data?.months || []).filter(m => {
      const [y, mo] = (m.period || "").split("-");
      if (selYear && y !== selYear) return false;
      if (selMonth !== "all" && mo !== selMonth) return false;
      return true;
    });
  }, [state.data?.months, selMonth, selYear]);

  // Regroupement par année (ordre déchronologique — année en cours en premier)
  const monthsByYear = useMemo(() => {
    const byYear = new Map();
    filteredMonths.forEach(m => {
      const y = (m.period || "").slice(0, 4);
      if (!y) return;
      if (!byYear.has(y)) byYear.set(y, []);
      byYear.get(y).push(m);
    });
    // Les mois arrivent déjà triés desc du backend ; on trie juste les années desc
    return Array.from(byYear.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, months]) => ({
        year,
        months,
        totalDecl: months.reduce((s, m) => s + (m.declarations || []).filter(d => d.status !== "annule").length, 0),
        totalHt: months.reduce((s, m) => s + (m.total_ht || 0), 0),
      }));
  }, [filteredMonths]);

  // État des années dépliées — année en cours ouverte par défaut
  const currentYearStr = String(new Date().getFullYear());
  const [expandedYears, setExpandedYears] = useState(() => new Set([currentYearStr]));
  const toggleYear = (year) => setExpandedYears(prev => {
    const next = new Set(prev);
    if (next.has(year)) next.delete(year); else next.add(year);
    return next;
  });
  // Quand on filtre par année (selYear renseigné), déplier automatiquement cette année
  useEffect(() => {
    if (selYear && String(selYear) !== "all") {
      setExpandedYears(prev => new Set([...prev, String(selYear)]));
    }
  }, [selYear]);

  // État des dates dépliées — clé "YYYY-MM-DD"
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  const toggleDate = (date) => setExpandedDates(prev => {
    const next = new Set(prev);
    if (next.has(date)) next.delete(date); else next.add(date);
    return next;
  });

  // État des mois dépliés — mois en cours (YYYY-MM) ouvert par défaut,
  // et à défaut si aucune donnée pour le mois en cours, on ouvre le plus récent
  const currentPeriod = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [expandedMonths, setExpandedMonths] = useState(() => new Set([currentPeriod]));
  const toggleMonth = (period) => setExpandedMonths(prev => {
    const next = new Set(prev);
    if (next.has(period)) next.delete(period); else next.add(period);
    return next;
  });
  // Au chargement des données, déplie automatiquement le mois le plus récent s'il n'y a pas déjà un mois déplié dans les données
  useEffect(() => {
    if (!filteredMonths.length) return;
    const hasExpandedInData = filteredMonths.some(m => expandedMonths.has(m.period));
    if (!hasExpandedInData) {
      setExpandedMonths(prev => new Set([...prev, filteredMonths[0].period]));
    }
    // eslint-disable-next-line
  }, [filteredMonths.length ? filteredMonths[0]?.period : null]);
  // Quand on filtre par mois (selMonth renseigné), déplier automatiquement les mois affichés
  useEffect(() => {
    if (selMonth && String(selMonth) !== "all") {
      setExpandedMonths(prev => {
        const next = new Set(prev);
        filteredMonths.forEach(m => next.add(m.period));
        return next;
      });
    }
    // eslint-disable-next-line
  }, [selMonth]);


  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Chargement...</div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="max-w-md w-full border-border/60 shadow-warm-lg"><CardContent className="p-8 text-center">
        <div className="text-destructive text-4xl mb-3">✕</div>
        <h2 className="font-brand text-2xl mb-2">Lien inaccessible</h2>
        <p className="text-muted-foreground text-sm">{state.error}</p>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="border-b border-border/60 bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO} alt="Antoine Événements" className="h-10 w-auto" />
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Portail client</div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="client-portal">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ backgroundColor: color + "1A", color }}>
              <Building2 className="h-3 w-3" /> Portail — {client?.name}
            </div>
            <h1 className="font-brand text-3xl md:text-4xl tracking-tight mt-2">Mes déclarations</h1>
            <p className="text-sm text-muted-foreground mt-1">Suivez le statut de vos demandes, demandez une modification ou une annulation.</p>
          </div>
          {state.data?.request_declaration_url && (
            <div className="flex flex-col items-stretch md:items-end gap-2">
              {!isIntermittentPortal && (
                <button
                  type="button"
                  onClick={() => { setInscEmail(""); setInscOpen(true); }}
                  data-testid="portal-inscription-link-btn"
                  className="text-xs text-primary underline underline-offset-4 hover:no-underline font-semibold text-right"
                >
                  Inscrire un nouvel intermittent →
                </button>
              )}
              <a href={state.data.request_declaration_url} target="_blank" rel="noreferrer">
                <Button className="rounded-full h-11 px-5 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95 w-full md:w-auto" data-testid="portal-new-decl-btn">
                  <Send className="h-4 w-4 mr-2" /> Demander une nouvelle déclaration
                </Button>
              </a>
            </div>
          )}
        </div>

        {/* Lien d'inscription intermittent — retiré à la demande du client */}

        {/* Portail INTERMITTENT : réglages + calendrier + compteur cachets */}
        {isIntermittentPortal && (
          <Card className="border-2 border-primary/30 shadow-warm bg-gradient-to-br from-primary/5 to-transparent" data-testid="intermittent-settings-card">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
                <CalIcon className="h-3.5 w-3.5" style={{ color }} /> Vos réglages & compteur annuel
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Renewal date : JJ / MM uniquement */}
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Date de renouvellement (JJ / MM)</Label>
                  <div className="mt-1 flex gap-2">
                    <select
                      value={settingsForm.renewal_day}
                      onChange={e => setSettingsForm(s => ({ ...s, renewal_day: e.target.value }))}
                      className="flex-1 rounded-md border border-input bg-background px-2 py-2 text-sm"
                      data-testid="settings-renewal-day"
                    >
                      <option value="">Jour</option>
                      {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map(d => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    <select
                      value={settingsForm.renewal_month}
                      onChange={e => setSettingsForm(s => ({ ...s, renewal_month: e.target.value }))}
                      className="flex-1 rounded-md border border-input bg-background px-2 py-2 text-sm"
                      data-testid="settings-renewal-month"
                    >
                      <option value="">Mois</option>
                      {MONTHS_FR.map((m, i) => (
                        <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Jour anniversaire — la fenêtre de comptage couvre les 12 mois avant.
                  </div>
                </div>
                {/* Default km rate */}
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Prix par défaut au km (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={settingsForm.default_cost_per_km}
                    onChange={e => setSettingsForm(s => ({ ...s, default_cost_per_km: e.target.value }))}
                    className="mt-1"
                    data-testid="settings-default-km"
                  />
                  <div className="text-[10px] text-muted-foreground mt-1">
                    Auto-appliqué sur tous vos futurs frais « Voiture perso ».
                  </div>
                </div>
                {/* Cachet counter */}
                <div className="rounded-xl border-2 border-primary/40 bg-card p-3" data-testid="cachet-counter-tile">
                  <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>Cachets sur la période</div>
                  <div className="font-display text-4xl font-semibold tabular-nums leading-none mt-1" style={{ color }}>{cachetCount}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {cachetWindow ? (
                      <>Du {frDate(cachetWindow.start)} au {frDate(cachetWindow.end)}</>
                    ) : "Année civile en cours"}
                  </div>
                </div>
                {/* Save button */}
                <div className="flex items-end">
                  <Button
                    className="w-full rounded-full h-11 bg-primary text-primary-foreground disabled:opacity-40"
                    onClick={saveSettings}
                    disabled={!settingsDirty || savingSettings}
                    data-testid="settings-save-btn"
                  >
                    {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    {settingsDirty ? "Enregistrer les réglages" : "Réglages à jour"}
                  </Button>
                </div>
              </div>
              {/* Filtre mois/année déplacé hors de cette carte (visible pour tous les portails) */}
            </CardContent>
          </Card>
        )}

        {/* Filtre période — visible sur TOUS les portails (client + intermittent) */}
        <Card className="border-border/60 shadow-warm" data-testid="portal-period-filter">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
              <CalIcon className="h-3 w-3" style={{ color }} /> Afficher la période
            </div>
            <select
              value={selMonth}
              onChange={e => setSelMonth(e.target.value)}
              className="rounded-full border border-border/60 bg-card px-3 py-1.5 text-sm font-medium"
              data-testid="filter-month"
            >
              <option value="all">Toute l'année</option>
              {MONTHS_FR.map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
              ))}
            </select>
            <select
              value={selYear}
              onChange={e => setSelYear(e.target.value)}
              className="rounded-full border border-border/60 bg-card px-3 py-1.5 text-sm font-medium"
              data-testid="filter-year"
            >
              {availableYears.map(y => (<option key={y} value={y}>{y}</option>))}
            </select>
            <span className="text-[11px] text-muted-foreground ml-auto">
              {filteredMonths.length} mois affiché(s) · {filteredMonths.reduce((s, m) => s + m.declarations.filter(d => d.status !== "annule").length, 0)} déclarations
            </span>
          </CardContent>
        </Card>

        <Card className="border-border/60 shadow-warm">
          <CardContent className="p-6 flex flex-wrap gap-8">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Total facturé HT (toutes périodes)</div>
              <div className="font-display text-4xl font-semibold tabular-nums mt-1" style={{ color }} data-testid="portal-total-ht">{grandTotal.toLocaleString("fr-FR")} €</div>
              <div className="text-[10px] text-muted-foreground mt-1">Hors contrats annulés</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Déclarations</div>
              <div className="font-display text-4xl font-semibold tabular-nums mt-1">
                {(state.data?.months || []).reduce((s, m) => s + m.declarations.filter(d => d.status !== "annule").length, 0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Mois actifs</div>
              <div className="font-display text-4xl font-semibold tabular-nums mt-1">{(state.data?.months || []).length}</div>
            </div>
            {isIntermittentPortal && (
              <div data-testid="portal-total-expenses-tile">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
                  <Wallet className="h-3 w-3" /> Total frais
                </div>
                <div className="font-display text-4xl font-semibold tabular-nums mt-1" style={{ color }}>
                  {Number(grandExpenses).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Toutes périodes confondues</div>
              </div>
            )}
            {isIntermittentPortal && (
              <div data-testid="portal-km-year-tile">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1">
                  <Car className="h-3 w-3" /> Km {state.data?.km_year || new Date().getFullYear()}
                </div>
                <div className="font-display text-4xl font-semibold tabular-nums mt-1" style={{ color }}>
                  {Number(state.data?.km_year_total || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">Voiture perso · 01/01 → 31/12</div>
              </div>
            )}
            {(state.data?.cancelled_summary?.count || 0) > 0 && (
              <div className="border-l border-border/60 pl-8" data-testid="portal-cancelled-summary">
                <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold flex items-center gap-1">
                  <XCircle className="h-3 w-3" /> Contrats annulés
                </div>
                <div className="font-display text-4xl font-semibold tabular-nums mt-1 text-red-300">
                  {state.data.cancelled_summary.count}
                </div>
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {Number(state.data.cancelled_summary.total_ht || 0).toLocaleString("fr-FR")} € HT
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {(filteredMonths).length === 0 && (
          <Card className="border-border/60 shadow-warm"><CardContent className="p-10 text-center text-muted-foreground">
            {isIntermittentPortal && (state.data?.months || []).length > 0
              ? "Aucune déclaration pour la période sélectionnée."
              : "Aucune déclaration enregistrée pour le moment."}
          </CardContent></Card>
        )}

        {monthsByYear.map(({ year, months: yearMonths, totalDecl, totalHt }) => {
          const isOpen = expandedYears.has(year);
          return (
            <div key={`year-${year}`} className="space-y-4" data-testid={`portal-year-${year}`}>
              <button
                type="button"
                onClick={() => toggleYear(year)}
                className={`w-full flex items-center gap-3 px-5 py-4 rounded-xl border-2 transition active:scale-[0.995] ${isOpen ? "bg-primary/10 border-primary/40" : "bg-primary/5 border-primary/20 hover:bg-primary/10"}`}
                data-testid={`portal-year-toggle-${year}`}
              >
                {isOpen ? <ChevronDown className="h-5 w-5 text-primary shrink-0" /> : <ChevronRight className="h-5 w-5 text-primary shrink-0" />}
                <span className="font-brand font-bold text-2xl md:text-3xl text-primary tracking-tight">{year}</span>
                <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold hidden md:inline">
                  {yearMonths.length} mois · {totalDecl} déclaration{totalDecl > 1 ? "s" : ""}
                </span>
                <span className="ml-auto font-display font-semibold tabular-nums text-primary text-lg">
                  {totalHt.toLocaleString("fr-FR")} €
                </span>
              </button>
              {isOpen && yearMonths.map(m => {
                const isMonthOpen = expandedMonths.has(m.period);
                return (
          <Card key={m.period} className="border-border/60 shadow-warm" data-testid={`portal-month-${m.period}`}>
            <CardContent className="p-0">
              <div
                className="flex items-center justify-between px-6 py-4 border-b-2 cursor-pointer"
                style={{ backgroundColor: color + "1a", borderBottomColor: color + "55" }}
                data-testid={`portal-month-header-${m.period}`}
                onClick={() => toggleMonth(m.period)}
              >
                <div className="flex items-center gap-3">
                  {isMonthOpen ? <ChevronDown className="h-4 w-4 shrink-0" style={{ color }} /> : <ChevronRight className="h-4 w-4 shrink-0" style={{ color }} />}
                  <CalIcon className="h-4 w-4" style={{ color }} />
                  <div className="font-display font-semibold text-lg capitalize" style={{ color }}>{periodLabel(m.period)}</div>
                  <span className="text-xs text-muted-foreground tabular-nums" data-testid={`portal-month-count-${m.period}`}>
                    {((m.count_active ?? m.declarations.length) + (m.count_cancelled || 0))} contrat(s){(m.count_cancelled || 0) > 0 ? ` dont ${m.count_cancelled} annulé${m.count_cancelled > 1 ? "s" : ""}` : ""}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Total HT mensuel</div>
                    <div className="font-display text-xl font-semibold tabular-nums" style={{ color }}>{m.total_ht.toLocaleString("fr-FR")} €</div>
                  </div>
                  {isIntermittentPortal && Number(m.total_expenses || 0) > 0 && (
                    <div className="text-right border-l border-border/60 pl-3" data-testid={`portal-month-expenses-${m.period}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 justify-end">
                        <Wallet className="h-3 w-3" /> Frais du mois
                      </div>
                      <div className="font-display text-xl font-semibold tabular-nums text-foreground">
                        {Number(m.total_expenses).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                    </div>
                  )}
                  {isIntermittentPortal && Number(m.km_total || 0) > 0 && (
                    <div className="text-right border-l border-border/60 pl-3" data-testid={`portal-month-km-${m.period}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 justify-end">
                        <Car className="h-3 w-3" /> Km du mois
                      </div>
                      <div className="font-display text-xl font-semibold tabular-nums" style={{ color }}>
                        {Number(m.km_total).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={(e) => { e.stopPropagation(); downloadMonthPdf(m.period); }}
                    data-testid={`portal-download-${m.period}`}
                  >
                    <Download className="h-3.5 w-3.5 mr-1" /> PDF récapitulatif
                  </Button>
                </div>
              </div>
              {isMonthOpen && (
              <div className="divide-y divide-border/40">
                {groupByDateThenEvent(m.declarations).map((dateGroup) => {
                  const dateKey = dateGroup.date || `no-date-${m.period}`;
                  const isDateOpen = expandedDates.has(dateKey);
                  return (
                  <div key={dateKey} className="px-6 py-3" data-testid={`portal-date-${dateGroup.date || "none"}`}>
                    {/* En-tête DATE — cliquable pour déplier / replier */}
                    <button
                      type="button"
                      onClick={() => toggleDate(dateKey)}
                      className="w-full flex items-center gap-3 pb-2 border-b border-border/40 text-left hover:bg-muted transition rounded-md px-1 -mx-1 py-1"
                      data-testid={`portal-date-toggle-${dateGroup.date || "none"}`}
                    >
                      {isDateOpen ? <ChevronDown className="h-4 w-4 shrink-0" style={{ color }} /> : <ChevronRight className="h-4 w-4 shrink-0" style={{ color }} />}
                      <div className="font-brand font-bold tracking-tight text-2xl md:text-3xl tabular-nums leading-none" style={{ color }}>
                        {dateGroup.date ? dateGroup.date.slice(8, 10) : "—"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm md:text-base font-display font-medium capitalize leading-tight">
                          {longDateLabel(dateGroup.date)}
                          <span className="text-muted-foreground font-normal ml-1">
                            {dateGroup.date ? dateGroup.date.slice(0, 4) : ""}
                          </span>
                        </div>
                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold">
                          {dateGroup.declarations_count} déclaration{dateGroup.declarations_count > 1 ? "s" : ""} · {dateGroup.events.length} événement{dateGroup.events.length > 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold leading-none">Total HT jour</div>
                        <div className="text-base font-display font-semibold tabular-nums" style={{ color }}>{dateGroup.total_ht.toLocaleString("fr-FR")} €</div>
                      </div>
                    </button>

                    {isDateOpen && (
                    /* Sous-rubrique par événement */
                    <div className="space-y-4 mt-3">
                      {dateGroup.events.map((ev, idx) => (
                        <div key={idx} className="rounded-xl bg-muted border border-border/60 overflow-hidden">
                          <div className="px-4 py-2.5 bg-muted border-b border-border/60 flex items-center justify-between gap-2">
                            <div className="min-w-0 flex items-center gap-2 flex-1">
                              <Building2 className="h-3.5 w-3.5 shrink-0" style={{ color }} />
                              <span className="font-display font-semibold truncate">{ev.event_name || "Prestation"}</span>
                              {ev.event_location && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                  <MapPin className="h-3 w-3 shrink-0" /> {ev.event_location}
                                </span>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold leading-none">Sous-total HT</div>
                              <div className="text-sm font-semibold tabular-nums" style={{ color }}>{ev.total_ht.toLocaleString("fr-FR")} €</div>
                            </div>
                          </div>
                          <div className="divide-y divide-border/40">
                            {ev.declarations.map(d => (
                              <div key={d.id} className="px-4 py-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`portal-decl-${d.id}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium">{d.intermittent_name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    {d.end_date && d.end_date !== d.start_date ? `Jusqu'au ${frDate(d.end_date)}` : ""}
                                    {d.start_time ? ` · ${d.start_time}` : ""}
                                    {d.role ? ` · ${d.role}` : ""}
                                  </div>
                                  {Number(d.cachet_hours || d.hours_cachet || 0) > 0 && (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                      <Clock className="h-3 w-3" />
                                      <span className="font-semibold text-foreground tabular-nums">
                                        {Number(d.cachet_count || 1)}
                                      </span>
                                      {Number(d.cachet_count || 1) > 1 ? "Cachets" : "Cachet"} de&nbsp;:
                                      <span className="font-semibold text-foreground tabular-nums">
                                        {Number(d.cachet_hours || d.hours_cachet || 0).toLocaleString("fr-FR")} h
                                      </span>
                                    </div>
                                  )}
                                  {d.last_change?.status === "rejected" && (
                                    <div
                                      className="mt-1.5 rounded-md border border-orange-500/50 bg-orange-500/15 px-2 py-1 text-[11px] text-orange-200"
                                      data-testid={`portal-rejected-inline-${d.id}`}
                                    >
                                      <span className="font-bold uppercase tracking-wider text-[9px] text-orange-200">
                                        Demande {d.last_change.action === "cancel" ? "d'annulation" : "de modification"} refusée
                                      </span>
                                      {d.last_change.resolved_at && (
                                        <span className="ml-1 text-orange-300">le {frDate(d.last_change.resolved_at.slice(0, 10))}</span>
                                      )}
                                      {d.last_change.rejection_reason && (
                                        <div className="italic mt-0.5">Motif : « {d.last_change.rejection_reason} »</div>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                  {d.status !== "annule" && (
                                    <Badge variant="outline" className={`rounded-full text-xs font-semibold ${STATUS_CLASS[d.status] || ""}`} data-testid={`portal-status-${d.id}`}>{STATUS_LABEL[d.status] || d.status}</Badge>
                                  )}
                                  {d.last_change?.status === "handled" && d.last_change?.action === "modify" && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          className="inline-flex items-center rounded-full text-[10px] bg-blue-500/15 text-blue-200 border border-blue-500/50 font-semibold px-2 py-0.5 hover:bg-blue-200 transition-colors cursor-pointer"
                                          data-testid={`portal-modified-${d.id}`}
                                          title="Cliquez pour voir la modification demandée"
                                        >
                                          <Pencil className="h-3 w-3 mr-1" /> Contrat modifié
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80" side="top">
                                        <div className="space-y-2">
                                          <div className="text-[10px] uppercase tracking-widest font-bold text-blue-200 flex items-center gap-1">
                                            <Pencil className="h-3 w-3" /> Modification approuvée
                                          </div>
                                          {d.last_change.resolved_at && (
                                            <div className="text-[11px] text-muted-foreground">Le {d.last_change.resolved_at.slice(0,10)}</div>
                                          )}
                                          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground pt-1">Demande initiale</div>
                                          <div className="text-sm bg-blue-500/15 border border-blue-500/40 rounded-md px-3 py-2 whitespace-pre-line">
                                            {d.last_change.message || "—"}
                                          </div>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                  {d.status === "annule" && (
                                    <Badge
                                      className="rounded-full text-[10px] bg-red-500/15 text-red-200 border-red-400 font-semibold"
                                      data-testid={`portal-cancelled-${d.id}`}
                                    >
                                      <XCircle className="h-3 w-3 mr-1" /> Contrat annulé
                                    </Badge>
                                  )}
                                  {d.last_change?.status === "rejected" && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          className="inline-flex items-center rounded-full text-[10px] bg-orange-500/15 text-orange-200 border border-orange-400 font-semibold px-2 py-0.5 hover:bg-orange-200 transition-colors cursor-pointer"
                                          data-testid={`portal-rejected-${d.id}`}
                                          title="Cliquez pour voir le motif du refus"
                                        >
                                          <Info className="h-3 w-3 mr-1" /> Demande refusée
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-80" side="top">
                                        <div className="space-y-2">
                                          <div className="text-[10px] uppercase tracking-widest font-bold text-orange-200 flex items-center gap-1">
                                            <Info className="h-3 w-3" /> Demande refusée
                                          </div>
                                          {d.last_change.resolved_at && (
                                            <div className="text-[11px] text-muted-foreground">Le {d.last_change.resolved_at.slice(0,10)}</div>
                                          )}
                                          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground pt-1">Motif</div>
                                          <div className="text-sm bg-orange-500/15 border border-orange-500/40 rounded-md px-3 py-2 whitespace-pre-line">
                                            {d.last_change.rejection_reason || "—"}
                                          </div>
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                  {d.status === "annule" ? null : d.invoice_paid ? (
                                    <Badge className="rounded-full text-[10px] bg-emerald-500/15 text-emerald-200 border-emerald-400 font-semibold" data-testid={`portal-invoice-badge-${d.id}`}>
                                      <CheckCircle2 className="h-3 w-3 mr-1" /> Facture payée
                                    </Badge>
                                  ) : d.invoice_sent ? (
                                    <Badge className="rounded-full text-[10px] bg-primary/10 text-primary border-primary/40 font-semibold" data-testid={`portal-invoice-badge-${d.id}`}>
                                      <FileText className="h-3 w-3 mr-1" /> Facture envoyée
                                    </Badge>
                                  ) : (
                                    <Badge className="rounded-full text-[10px] bg-secondary text-secondary-foreground border-secondary font-semibold" data-testid={`portal-invoice-badge-${d.id}`}>
                                      À facturer
                                    </Badge>
                                  )}
                                  <div className="text-right min-w-[80px]">
                                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">HT</div>
                                    <div className="text-sm font-semibold tabular-nums">{Number(d.cost_ht_client || 0).toLocaleString("fr-FR")} €</div>
                                  </div>
                                  {isIntermittentPortal && (
                                    <div className="flex items-center gap-2" data-testid={`portal-expenses-cell-${d.id}`}>
                                      {Number(d.expenses_total || 0) > 0 && (
                                        <div className="text-right min-w-[80px] px-2 py-1 rounded-md bg-muted border border-border/60">
                                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 justify-end leading-none">
                                            <Wallet className="h-3 w-3" /> Frais
                                          </div>
                                          <div className="text-sm font-semibold tabular-nums leading-tight">
                                            {Number(d.expenses_total).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                          </div>
                                        </div>
                                      )}
                                      {d.status !== "annule" && (() => {
                                        const needsExpenses = d.expenses_expected === true
                                          && (d.expenses_count || 0) === 0
                                          && d.status !== "contrat_a_faire"
                                          && d.expenses_submitted !== true;
                                        return (
                                          <Button
                                            variant={needsExpenses ? "default" : "outline"}
                                            size="sm"
                                            className={`rounded-full ${needsExpenses ? "bg-secondary text-secondary-foreground hover:bg-secondary/90 border-2 border-primary shadow-glow expenses-cta-pulse font-bold" : ""}`}
                                            onClick={() => openExpenses(d)}
                                            data-testid={`portal-expenses-btn-${d.id}`}
                                            title={needsExpenses ? "Frais prévus sur ce contrat — cliquez pour les saisir" : "Gérer les frais de ce contrat"}
                                          >
                                            <Receipt className="h-3.5 w-3.5 mr-1" />
                                            {needsExpenses
                                              ? "Frais à saisir !"
                                              : `Frais${d.expenses_count > 0 ? ` (${d.expenses_count})` : ""}${d.expenses_submitted ? " ✓" : ""}`}
                                          </Button>
                                        );
                                      })()}
                                    </div>
                                  )}
                                  {d.pending_change ? (
                                    <Badge
                                      className={`rounded-full text-xs font-semibold ${d.pending_change.action === "cancel" ? "bg-red-500/15 text-red-200 border-red-400" : "bg-amber-500/15 text-amber-200 border-amber-400"}`}
                                      title={d.pending_change.message ? `Votre message : ${d.pending_change.message}` : undefined}
                                      data-testid={`portal-pending-${d.id}`}
                                    >
                                      {d.pending_change.action === "cancel" ? (
                                        <><XCircle className="h-3 w-3 mr-1" /> Demande d'annulation en cours</>
                                      ) : (
                                        <><Pencil className="h-3 w-3 mr-1" /> Demande de modification en cours</>
                                      )}
                                    </Badge>
                                  ) : d.status === "annule" ? null : (
                                    <>
                                      <Button variant="outline" size="sm" className="rounded-full" onClick={() => openChange(d, "modify")} data-testid={`portal-modify-${d.id}`}>
                                        <Pencil className="h-3.5 w-3.5 mr-1" /> Modifier
                                      </Button>
                                      <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:bg-destructive/10" onClick={() => openChange(d, "cancel")} data-testid={`portal-cancel-${d.id}`}>
                                        <XCircle className="h-3.5 w-3.5 mr-1" /> Annuler
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                  );
                })}
              </div>
              )}
            </CardContent>
          </Card>
                );
              })}
            </div>
          );
        })}

        <div className="text-center text-xs text-muted-foreground mt-8">
          Agence Antoine Événements · <a href="https://www.antoineevenements.fr" target="_blank" rel="noreferrer" className="text-primary underline">antoineevenements.fr</a>
        </div>
      </div>

      <Dialog open={changeOpen} onOpenChange={setChangeOpen}>
        <DialogContent className="max-w-lg" data-testid="portal-change-dialog">
          <DialogHeader>
            <DialogTitle>{changeForm.action === "cancel" ? "Demander l'annulation" : "Demander une modification"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Décrivez ce qui doit changer (dates, poste, montant, remplacement d'intermittent…). Notre équipe traitera votre demande dans les meilleurs délais.</p>
            <div>
              <Label>Votre message</Label>
              <Textarea rows={5} value={changeForm.message} onChange={e => setChangeForm(f => ({ ...f, message: e.target.value }))} placeholder="Ex : Reporter la prestation au vendredi 15 août, mêmes conditions." data-testid="portal-change-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setChangeOpen(false)}>Annuler</Button>
            <Button
              className="rounded-full bg-primary active:scale-95"
              onClick={submitChange}
              disabled={submitting}
              data-testid="portal-change-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-md" data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Envoyer l'invitation d'inscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email de l'intermittent *</Label>
              <Input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="prenom.nom@exemple.fr" data-testid="invite-email" />
            </div>
            <div>
              <Label>Prénom (optionnel — pour personnaliser l'email)</Label>
              <Input value={inviteForm.name} onChange={e => setInviteForm(f => ({ ...f, name: e.target.value }))} data-testid="invite-name" />
            </div>
            <div className="text-xs text-muted-foreground italic">Un email sera envoyé avec le lien d'inscription au nom d'Agence Antoine Événements.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setInviteOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-primary" onClick={sendInvite} disabled={inviteSending} data-testid="invite-send">
              {inviteSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              Envoyer l'invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==== Dialog Frais (portail intermittent) ==== */}
      <Dialog open={expensesOpen} onOpenChange={setExpensesOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="expenses-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" style={{ color }} />
              Frais du contrat
            </DialogTitle>
            {expensesDecl && (
              <div className="text-sm text-muted-foreground pt-1">
                {expensesDecl.event_name || "Prestation"}
                {expensesDecl.start_date && ` · ${frDate(expensesDecl.start_date)}`}
                {expensesDecl.client_company_name && ` · ${expensesDecl.client_company_name}`}
              </div>
            )}
          </DialogHeader>

          {/* Récapitulatif par type */}
          {expensesLoading ? (
            <div className="py-6 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Chargement…</div>
          ) : (
            <>
              {expensesData.expenses.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground border rounded-lg border-dashed">
                  Aucun frais enregistré pour ce contrat.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2" data-testid="expenses-totals-by-category">
                    {(expensesData.totals_by_category || []).map(c => (
                      <div key={c.category} className="px-3 py-1.5 rounded-full bg-muted border border-border/60 text-xs">
                        <span className="font-semibold">{c.category}</span>
                        <span className="text-muted-foreground"> · {c.count} frais</span>
                        <span className="ml-2 font-bold tabular-nums" style={{ color }}>
                          {Number(c.total).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </span>
                      </div>
                    ))}
                    <div className="ml-auto px-3 py-1.5 rounded-full font-bold text-white text-xs" style={{ backgroundColor: color }} data-testid="expenses-total">
                      Total : {Number(expensesData.total).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </div>
                  </div>
                  <div className="divide-y divide-border/60 border border-border/60 rounded-lg overflow-hidden" data-testid="expenses-list">
                    {expensesData.expenses.map(e => {
                      const isEditing = editingId === e.id;
                      if (!isEditing) return (
                      <div key={e.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted" data-testid={`expense-row-${e.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{e.title}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                            <span>{e.expense_label}</span>
                            {e.expense_category && e.expense_category !== e.expense_label && (
                              <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-semibold uppercase tracking-wider">
                                {e.expense_category}
                              </span>
                            )}
                            {e.expense_type === "transport_voiture" && (
                              <span>· {e.km_count} km × {Number(e.cost_per_km).toFixed(2)}€/km</span>
                            )}
                            {e.receipt_file_id && (
                              <a
                                href={`${API_BASE}/public-links/${token}/files/${e.receipt_file_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline inline-flex items-center gap-1"
                                data-testid={`expense-receipt-${e.id}`}
                              >
                                <FileText className="h-3 w-3" /> Justificatif
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-bold tabular-nums shrink-0">
                          {Number(e.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-primary hover:bg-primary/10 h-8 w-8"
                          onClick={() => startEditExpense(e)}
                          data-testid={`expense-edit-${e.id}`}
                          title="Modifier ce frais"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10 h-8 w-8"
                          onClick={() => deleteExpense(e.id)}
                          data-testid={`expense-del-${e.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      );
                      // ===== Mode édition inline =====
                      const isCarEdit = editForm.expense_type === "transport_voiture";
                      const hasReceipt = !editReceiptCleared && (editReceiptFile || e.receipt_file_id);
                      return (
                        <div key={e.id} className="px-3 py-3 bg-secondary/10 border-t-2 border-primary/40 space-y-3" data-testid={`expense-edit-row-${e.id}`}>
                          <div className="text-xs uppercase tracking-widest text-primary font-bold flex items-center gap-1">
                            <Pencil className="h-3 w-3" /> Modification du frais
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Nom du frais *</Label>
                              <Input
                                value={editForm.title}
                                onChange={ev => setEditForm(f => ({ ...f, title: ev.target.value }))}
                                data-testid={`expense-edit-title-${e.id}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Type de frais *</Label>
                              <select
                                value={editForm.expense_type}
                                onChange={ev => setEditForm(f => ({ ...f, expense_type: ev.target.value }))}
                                className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm"
                                data-testid={`expense-edit-type-${e.id}`}
                              >
                                {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                              </select>
                            </div>
                            {isCarEdit ? (
                              <>
                                <div>
                                  <Label className="text-xs">Nombre de kilomètres *</Label>
                                  <Input
                                    type="number" min="0" step="0.1"
                                    value={editForm.km_count}
                                    onChange={ev => setEditForm(f => ({ ...f, km_count: ev.target.value }))}
                                    data-testid={`expense-edit-km-${e.id}`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Coût par km (€) *</Label>
                                  <Input
                                    type="number" min="0" step="0.01"
                                    value={editForm.cost_per_km}
                                    onChange={ev => setEditForm(f => ({ ...f, cost_per_km: ev.target.value }))}
                                    data-testid={`expense-edit-cpk-${e.id}`}
                                  />
                                </div>
                                <div className="md:col-span-2 text-xs text-muted-foreground">
                                  Total automatique :{" "}
                                  <span className="font-semibold text-foreground tabular-nums">
                                    {((parseFloat(editForm.km_count) || 0) * (parseFloat(editForm.cost_per_km) || 0)).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                                  </span>
                                </div>
                              </>
                            ) : (
                              <div>
                                <Label className="text-xs">Montant (€) *</Label>
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={editForm.amount}
                                  onChange={ev => setEditForm(f => ({ ...f, amount: ev.target.value }))}
                                  data-testid={`expense-edit-amount-${e.id}`}
                                />
                              </div>
                            )}
                            <div className="md:col-span-2">
                              <Label className="text-xs">Justificatif</Label>
                              <div className="mt-1 flex items-center gap-2 flex-wrap">
                                <label
                                  className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-card px-3 h-9 text-sm cursor-pointer hover:bg-primary/5 text-primary font-semibold active:scale-95 transition"
                                  data-testid={`expense-edit-receipt-btn-${e.id}`}
                                >
                                  <FileText className="h-4 w-4" />
                                  {hasReceipt ? "Remplacer le justificatif" : "Ajouter un justificatif"}
                                  <input
                                    type="file"
                                    accept="application/pdf,image/*"
                                    className="hidden"
                                    onChange={ev => { setEditReceiptFile(ev.target.files?.[0] || null); setEditReceiptCleared(false); }}
                                  />
                                </label>
                                {editReceiptFile ? (
                                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                                    <FileText className="h-3 w-3" /> Nouveau : {editReceiptFile.name}
                                  </div>
                                ) : (!editReceiptCleared && e.receipt_file_id) ? (
                                  <>
                                    <a
                                      href={`${API_BASE}/public-links/${token}/files/${e.receipt_file_id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-primary underline text-xs inline-flex items-center gap-1"
                                    >
                                      <FileText className="h-3 w-3" /> Justificatif actuel
                                    </a>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive hover:bg-destructive/10 h-8 rounded-full text-xs"
                                      onClick={() => { setEditReceiptCleared(true); setEditReceiptFile(null); }}
                                      data-testid={`expense-edit-receipt-remove-${e.id}`}
                                    >
                                      <Trash2 className="h-3 w-3 mr-1" /> Retirer
                                    </Button>
                                  </>
                                ) : editReceiptCleared ? (
                                  <span className="text-xs text-destructive italic">Justificatif retiré</span>
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">Aucun justificatif</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={cancelEditExpense}
                              disabled={savingEdit}
                              data-testid={`expense-edit-cancel-${e.id}`}
                            >
                              Annuler
                            </Button>
                            <Button
                              size="sm"
                              className="rounded-full bg-primary active:scale-95"
                              onClick={() => submitEditExpense(e)}
                              disabled={savingEdit}
                              data-testid={`expense-edit-save-${e.id}`}
                            >
                              {savingEdit ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                              Enregistrer
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Formulaire d'ajout */}
              <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-3">
                <div className="text-sm font-semibold flex items-center gap-2">
                  <Plus className="h-4 w-4" style={{ color }} /> Ajouter un frais
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Nom du frais *</Label>
                    <Input
                      value={expenseForm.title}
                      onChange={e => setExpenseForm(f => ({ ...f, title: e.target.value }))}
                      placeholder="Ex : Aller-retour Paris-Lyon"
                      data-testid="expense-title"
                    />
                  </div>
                  <div>
                    <Label>Type de frais *</Label>
                    <select
                      value={expenseForm.expense_type}
                      onChange={e => setExpenseForm(f => ({ ...f, expense_type: e.target.value }))}
                      className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm"
                      data-testid="expense-type"
                    >
                      {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>

                  {expenseForm.expense_type === "transport_voiture" ? (
                    <>
                      <div>
                        <Label>Nombre de kilomètres *</Label>
                        <Input
                          type="number" min="0" step="0.1"
                          value={expenseForm.km_count}
                          onChange={e => setExpenseForm(f => ({ ...f, km_count: e.target.value }))}
                          data-testid="expense-km"
                        />
                      </div>
                      <div>
                        <Label>Coût par km (€) *</Label>
                        <Input
                          type="number" min="0" step="0.01"
                          value={expenseForm.cost_per_km}
                          onChange={e => setExpenseForm(f => ({ ...f, cost_per_km: e.target.value }))}
                          data-testid="expense-cpk"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <div className="text-xs text-muted-foreground">
                          Total automatique :{" "}
                          <span className="font-semibold text-foreground tabular-nums" data-testid="expense-total-preview">
                            {(
                              (parseFloat(expenseForm.km_count) || 0) *
                              (parseFloat(expenseForm.cost_per_km) || 0)
                            ).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label>Montant (€) *</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={expenseForm.amount}
                        onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))}
                        data-testid="expense-amount"
                      />
                    </div>
                  )}

                  <div className="md:col-span-2">
                    <Label>Justificatif (PDF ou image, optionnel)</Label>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <label
                        className="inline-flex items-center gap-2 rounded-full border-2 border-primary bg-card px-4 h-10 text-sm cursor-pointer hover:bg-primary/5 text-primary font-semibold active:scale-95 transition"
                        data-testid="expense-receipt-btn"
                      >
                        <FileText className="h-4 w-4" />
                        {receiptFile ? "Remplacer le justificatif" : "Ajouter un justificatif"}
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          className="hidden"
                          onChange={e => setReceiptFile(e.target.files?.[0] || null)}
                          data-testid="expense-receipt-file"
                        />
                      </label>
                      {receiptFile && (
                        <>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <FileText className="h-3 w-3" /> {receiptFile.name}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10 h-8 rounded-full text-xs"
                            onClick={() => setReceiptFile(null)}
                            data-testid="expense-receipt-clear"
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Retirer
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={submitExpense}
                    disabled={savingExpense}
                    className="rounded-full bg-primary active:scale-95"
                    data-testid="expense-submit"
                  >
                    {savingExpense ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                    Ajouter le frais
                  </Button>
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            {expensesDecl && (
              <Button
                variant={expensesDecl.expenses_submitted ? "outline" : "default"}
                onClick={toggleExpensesSubmitted}
                disabled={markingSubmitted}
                className={`rounded-full mr-auto ${expensesDecl.expenses_submitted ? "border-primary text-primary" : "bg-secondary text-secondary-foreground hover:bg-secondary/90 border-2 border-primary font-bold"}`}
                data-testid="expense-mark-submitted"
                title={expensesDecl.expenses_submitted ? "Rouvrir la saisie des frais" : "Déclarer que tous les frais du contrat ont été déposés"}
              >
                {markingSubmitted ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : expensesDecl.expenses_submitted ? (
                  <RotateCcw className="h-4 w-4 mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                {expensesDecl.expenses_submitted ? "Rouvrir la saisie" : "J'ai déposé tous mes frais"}
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setExpensesOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogue Inscription intermittent (portail entreprise) */}
      <Dialog open={inscOpen} onOpenChange={setInscOpen}>
        <DialogContent className="max-w-lg" data-testid="portal-inscription-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-primary" /> Inscrire un nouvel intermittent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-muted border border-border/60 p-3">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Lien du formulaire d'inscription</Label>
              <div className="mt-1 font-mono text-sm break-all text-primary">{INSCRIPTION_URL}</div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 rounded-full border-primary/40 text-primary hover:bg-primary/10"
                onClick={copyInscriptionLink}
                data-testid="portal-copy-inscription-link"
              >
                <Copy className="h-4 w-4 mr-2" /> Copier le lien
              </Button>
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => window.open(INSCRIPTION_URL, "_blank")}
                data-testid="portal-open-inscription-link"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir
              </Button>
            </div>
            <div className="border-t border-border/60 pt-4">
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Envoyer par email</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="email"
                  placeholder="destinataire@exemple.fr"
                  value={inscEmail}
                  onChange={e => setInscEmail(e.target.value)}
                  data-testid="portal-inscription-email-input"
                />
                <Button
                  className="rounded-full bg-primary active:scale-95"
                  disabled={inscSending}
                  onClick={sendInscriptionEmail}
                  data-testid="portal-send-inscription-email"
                >
                  {inscSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                  Envoyer
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">Email envoyé depuis <strong>contact@antoineevenements.fr</strong> avec le lien et un message d'invitation.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setInscOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
